import { Button } from "@/components/ui/button";
import { Markdown } from "@/components/ui/markdown";
import { WikiSidebar } from "@/components/wiki/wiki-sidebar";
import { ApiError, api } from "@/lib/api";
import { preprocessWikiMarkdown } from "@/lib/wiki-markdown";
import { Link, createFileRoute, notFound, useLoaderData } from "@tanstack/react-router";

/**
 * `/projects/:slug/wiki/:wikiSlug` — a single wiki page, rendered
 * inside the project layout. Content is Hub's own copy (see
 * `routes/projects/wiki.ts`); a missing page surfaces as a 404.
 */

type WikiPageSummary = { slug: string; title: string };

type WikiPage = { slug: string; title: string; content: string };

type WikiPageResponse = {
  page: WikiPage;
  pages: WikiPageSummary[];
  sidebarMarkdown: string | null;
  canEdit: boolean;
};

export const Route = createFileRoute("/_app/projects/$slug/wiki/$wikiSlug/")({
  loader: async ({ params }) => {
    try {
      return await api.get<WikiPageResponse>(
        `/api/projects/${params.slug}/wiki/${encodeURIComponent(params.wikiSlug)}`,
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) throw notFound();
      throw err;
    }
  },
  component: WikiPageDetail,
});

function WikiPageDetail() {
  const { page, pages, sidebarMarkdown, canEdit } = Route.useLoaderData();
  const { slug } = Route.useParams();
  const { project } = useLoaderData({ from: "/_app/projects/$slug" });

  return (
    <div className="flex gap-6">
      <WikiSidebar
        slug={slug}
        pages={pages}
        sidebarMarkdown={sidebarMarkdown}
        currentSlug={page.slug}
      />

      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl font-bold">{page.title}</h2>
          <div className="flex items-center gap-2">
            {canEdit && (
              <Button variant="outline" size="sm" asChild>
                <Link
                  to="/projects/$slug/wiki/$wikiSlug/edit"
                  params={{ slug, wikiSlug: page.slug }}
                >
                  Edit
                </Link>
              </Button>
            )}
            {canEdit && (
              <Button size="sm" asChild>
                <Link to="/projects/$slug/wiki/new" params={{ slug }}>
                  New Page
                </Link>
              </Button>
            )}
          </div>
        </div>

        <div className="rounded-md border bg-card p-6">
          <Markdown
            content={preprocessWikiMarkdown(page.content, `/projects/${slug}/wiki`)}
            gitlabUploadProjectId={project.id}
          />
        </div>
      </div>
    </div>
  );
}
